/**
 * One fuelling station, every period — what it claimed, what it received, and
 * which dairies it received it from.
 *
 * The "from where" is the whole point. A station's claimed volume is an input
 * the dispensing feed asserts; what a filing has to defend is the attribution
 * behind it, and that attribution is a set of dairy-vintage lots the allocation
 * drew in feed order. Those lots are on the ledger already — for one period, one
 * station at a time. This is the same rows read down the other axis.
 *
 * Nothing here is fetched. `useStationHistory` reads the allocation the ledger
 * already built, so opening this drawer costs one replay the first time and
 * nothing after.
 */

import { Badge, Group, Paper, ScrollArea, Stack, Table, Text, Tooltip } from "@mantine/core";
import { useState } from "react";
import { useFseRegistry, useStationHistory } from "../../api/hooks";
import type { StationHistoryPeriod } from "../../api/types";
import { useBranding } from "../../auth/useBranding";
import { fmtMmbtu as fmt, isZeroMmbtu } from "../../lib/mmbtuFormat";
import { DetailRow } from "../DetailRow";
import { DetailTable, Empty, ExpandCell, HistoryDrawer, Mmbtu, TotalStat } from "./HistoryDrawer";

export function StationHistoryDrawer({
  fseId,
  onClose,
  onOpenDairy,
}: {
  fseId: string | null;
  onClose: () => void;
  /** Hands the reader across to the supply side without closing and re-finding. */
  onOpenDairy: (dairyId: string) => void;
}) {
  const { dispensing_partner_label: partner } = useBranding();
  const historyQ = useStationHistory(fseId);
  const registryQ = useFseRegistry();
  const record = registryQ.data?.items.find((r) => r.fse_id === fseId) ?? null;
  const history = historyQ.data;

  return (
    <HistoryDrawer
      opened={fseId !== null}
      onClose={onClose}
      title={history?.station_name ?? record?.station_name ?? fseId ?? ""}
      subtitle={
        <Text size="xs" c="dimmed" ff="monospace">
          {fseId}
          {record?.dispenser ? ` · ${record.dispenser}` : ""}
        </Text>
      }
      isPending={historyQ.isPending}
      error={historyQ.isError ? historyQ.error : null}
    >
      {history ? (
        <Stack gap="md">
          {record ? (
            <Paper p="md" radius="md" withBorder>
              <Group gap="xl" wrap="wrap">
                <DetailRow label="LCFS (LFF) ID" value={record.lff_id} mono />
                <DetailRow label={`${partner} FSE ID`} value={record.trillium_fse_id} mono />
                <DetailRow label="Utility" value={record.utility} />
                <DetailRow label="Meter" value={record.meter_number} mono />
                <DetailRow label="Address" value={record.address_normalized} />
              </Group>
            </Paper>
          ) : null}

          {history.periods.length === 0 ? (
            <Empty>
              This station has never claimed capacity or received volume in any period the ledger
              covers. It is registered, and that is all — a station carries no throughput of its
              own, only what a dispensing period attributes to it.
            </Empty>
          ) : (
            <>
              <Paper p="md" radius="md" withBorder>
                <Group justify="space-between" wrap="wrap" gap="lg">
                  <TotalStat
                    label="Received"
                    value={`${fmt(history.total_allocated.value)} MMBtu`}
                    sub={`over ${history.periods.length} period${history.periods.length === 1 ? "" : "s"}`}
                  />
                  <TotalStat
                    label="Claimed"
                    value={`${fmt(history.total_capacity.value)} MMBtu`}
                    sub="across the same periods"
                  />
                  <TotalStat
                    label="Supplied by"
                    value={String(history.by_dairy.length)}
                    sub={history.by_dairy.length === 1 ? "dairy" : "dairies"}
                  />
                  <TotalStat
                    label="Span"
                    value={history.last_period ?? "—"}
                    sub={`first dispensed ${history.first_period ?? "—"}`}
                  />
                </Group>
              </Paper>

              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
                  Allocation history
                </Text>
                <Text size="xs" c="dimmed">
                  Newest first. Open a period to see the dairy-vintage lots the allocation drew to
                  fill it — a vintage older than the period is volume that sat banked before this
                  station dispensed it.
                </Text>
              </Stack>

              <Paper radius="md" withBorder>
                <ScrollArea>
                  <Table highlightOnHover verticalSpacing="xs" miw={560}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Period</Table.Th>
                        <Table.Th ta="right">Claimed</Table.Th>
                        <Table.Th ta="right">Allocated</Table.Th>
                        <Table.Th ta="right">Residual</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {history.periods.map((period) => (
                        <PeriodRow key={period.period} period={period} onOpenDairy={onOpenDairy} />
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Paper>

              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
                  Lifetime by dairy
                </Text>
                <Paper radius="md" withBorder>
                  <Table verticalSpacing="xs">
                    <Table.Tbody>
                      {history.by_dairy.map((d) => (
                        <Table.Tr key={d.dairy_id}>
                          <Table.Td>
                            <Text
                              size="sm"
                              c="blue"
                              style={{ cursor: "pointer" }}
                              onClick={() => onOpenDairy(d.dairy_id)}
                            >
                              {d.dairy_name}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace" c="dimmed">
                              {d.fpc_code}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right">
                            <Mmbtu value={d.mmbtu.value} />
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              </Stack>
            </>
          )}
        </Stack>
      ) : null}
    </HistoryDrawer>
  );
}

function PeriodRow({
  period,
  onOpenDairy,
}: {
  period: StationHistoryPeriod;
  onOpenDairy: (dairyId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const clean = isZeroMmbtu(period.residual.value);

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <ExpandCell
            expanded={expanded}
            label={`Sources for ${period.period}`}
            onToggle={() => setExpanded((v) => !v)}
          >
            <Text size="sm" ff="monospace">
              {period.period}
            </Text>
            <Text size="xs" c="dimmed">
              {period.sources.length} lot{period.sources.length === 1 ? "" : "s"}
            </Text>
          </ExpandCell>
        </Table.Td>
        <Table.Td ta="right">
          <Mmbtu value={period.capacity.value} dim />
        </Table.Td>
        <Table.Td ta="right">
          <Mmbtu value={period.allocated.value} />
        </Table.Td>
        <Table.Td ta="right">
          {clean ? (
            <Text size="xs" c="dimmed">
              —
            </Text>
          ) : (
            <Tooltip
              label={
                period.queue_exhausted
                  ? "The lot queue ran dry this period: the dairies produced less than the stations claimed. A question about the claim, not a defect in the allocation."
                  : "Unallocated remainder while volume was still banked. That combination is an allocation fault."
              }
              multiline
              w={300}
              withArrow
            >
              <Badge color={period.queue_exhausted ? "yellow" : "orange"} variant="light" size="sm">
                {fmt(period.residual.value)}
              </Badge>
            </Tooltip>
          )}
        </Table.Td>
      </Table.Tr>

      {expanded ? (
        <DetailTable>
          {period.sources.length === 0 ? (
            <Table.Tr>
              <Table.Td>
                <Text size="xs" c="dimmed" pl="lg">
                  Capacity was claimed but nothing was allocated to it this period.
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            period.sources.map((row, idx) => (
              <Table.Tr key={`${row.dairy_id}:${row.vintage_month}:${idx}`}>
                <Table.Td pl="xl">
                  <Text
                    size="sm"
                    c="blue"
                    style={{ cursor: "pointer" }}
                    onClick={() => onOpenDairy(row.dairy_id)}
                  >
                    {row.dairy_name}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" ff="monospace">
                      {row.vintage_month}
                    </Text>
                    {row.vintage_month !== period.period ? (
                      <Tooltip
                        label={`Banked ${row.age_periods} period${row.age_periods === 1 ? "" : "s"} before this station dispensed it`}
                        withArrow
                      >
                        <Badge color="grape" variant="light" size="xs">
                          +{row.age_periods}
                        </Badge>
                      </Tooltip>
                    ) : null}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace" c="dimmed">
                    {row.fpc_code}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Mmbtu value={row.mmbtu.value} />
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </DetailTable>
      ) : null}
    </>
  );
}
